<script setup>
/* eslint-disable */
import { ref, computed } from 'vue'
import { useGameStore } from '@/store/game'
import { AuctionManager } from '@/services/auctionManager'
import ranchHandImage from '@/assets/ranch_hand.jpg'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { getChocoboColorClass, getWealthClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'
import { NPCBreedingManager } from '@/services/npcBreedingManager'
import { GaiaCalendar } from '@/utils/gaiaCalendar'

const emit = defineEmits(['close'])
const gameStore = useGameStore()
const expandedRegions = ref([])
const selectedRanch = ref(null)
const selectedChocobo = ref(null)
const negotiationOffer = ref(null)
const showNegotiationDialog = ref(false)
const showingAdvice = ref(false)
const ranchHandAdvice = ref('')

const getRankScale = (money) => {
  if (money < 15000) return { label: 'Scraping By', color: getWealthClass('scraping') }
  if (money < 30000) return { label: 'Making Do', color: getWealthClass('making_do') }
  if (money < 50000) return { label: 'Living Well', color: getWealthClass('living_well') }
  if (money < 75000) return { label: 'Well Off', color: getWealthClass('well_off') }
  if (money < 100000) return { label: 'Wealthy', color: getWealthClass('wealthy') }
  return { label: 'Mogul', color: getWealthClass('mogul') }
}

const getRegionName = (region) => {
  const regions = {
    'region1': 'Eastern Metropolis',
    'region2': 'Junon Area',
    'region3': 'Western Seaboard',
    'region4': 'Central Range',
    'region5': 'Mideel Forest',
    'region6': 'Northern Territory'
  }
  return regions[region] || region
}

// Group ranches by region
const ranchesByRegion = computed(() => {
  const grouped = {}
  
  gameStore.owners.forEach(owner => {
    if (!owner.isPlayer) {
      if (!grouped[owner.homeRegion]) {
        grouped[owner.homeRegion] = []
      }

      grouped[owner.homeRegion].push({
        ...owner,
        numChocobos: owner.chocoboIds.length,
        wealthStatus: getRankScale(owner.money),
        chocobos: gameStore.chocobos.filter(c => owner.chocoboIds.includes(c.id))
      })
    }
  })

  // Sort ranches within each region by wealth status
  for (const region in grouped) {
    grouped[region].sort((a, b) => b.money - a.money)
  }

  return grouped
})

const toggleRegion = (region) => {
  const index = expandedRegions.value.indexOf(region)
  if (index === -1) {
    expandedRegions.value.push(region)
  } else {
    expandedRegions.value.splice(index, 1)
  }
}

// Get breeding statistics for a specific owner
const getOwnerBreedingStats = (owner) => {
  const transactions = gameStore.npcBreedingTransactions || []
  const ownerBreedings = transactions.filter(t => t.ownerId === owner.id)
  
  const activeBreedings = gameStore.activeBreedings?.filter(b => 
    b.ownerId === owner.id && b.isNPC
  ) || []
  
  return {
    totalBreedings: ownerBreedings.length,
    activeBreedings: activeBreedings.length,
    totalSpent: ownerBreedings.reduce((sum, t) => sum + t.cost, 0),
    studIncome: owner.studIncome || 0,
    lastBreeding: ownerBreedings.length > 0 ? ownerBreedings[0].day : null
  }
}

// Get overall NPC breeding statistics
const overallBreedingStats = computed(() => {
  return NPCBreedingManager.getNPCBreedingStats(gameStore)
})

const unreadBreedingIncome = computed(() => {
  return gameStore.unreadBreedingIncome || []
})

const markAllBreedingIncomeAsRead = () => {
  if (gameStore.breedingIncomeNotifications) {
    gameStore.breedingIncomeNotifications.forEach(notification => {
      notification.read = true
    })
  }
}

const formatNotificationDate = (day) => {
  if (!day) return { date: 'N/A', daysAgo: 0 }
  
  const formattedDate = GaiaCalendar.formatGameDate(day)
  const daysAgo = gameStore.currentDay - day
  
  return {
    date: formattedDate,
    daysAgo: daysAgo
  }
}

</script>

<template>
  <div class="max-w-7xl mx-auto py-4 px-2 sm:px-4">
    <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
      <h2 :class="applyThemeClasses('text.primary')">Ranch Directory</h2>
      
      <!-- Breeding Economy Overview -->
      <div v-if="overallBreedingStats.totalNPCBreedings > 0" :class="`mb-6 p-4 rounded-lg border ${applyThemeClasses('layout.secondaryBackground')} ${applyThemeClasses('layout.border')}`">
        <h3 :class="`text-lg font-semibold mb-3 ${applyThemeClasses('text.primary')}`">🐣 Breeding Economy</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">{{ overallBreedingStats.activeNPCBreedings }}</div>
            <div :class="applyThemeClasses('text.secondary')">Active Breedings</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-green-600">{{ overallBreedingStats.totalNPCBreedings }}</div>
            <div :class="applyThemeClasses('text.secondary')">Total Bred</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-purple-600">{{ Math.round(overallBreedingStats.averageBreedingCost).toLocaleString() }}G</div>
            <div :class="applyThemeClasses('text.secondary')">Avg Cost</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-orange-600">{{ overallBreedingStats.totalStudFees.toLocaleString() }}G</div>
            <div :class="applyThemeClasses('text.secondary')">Stud Fees Earned</div>
          </div>
        </div>
        <div v-if="overallBreedingStats.mostActiveBreeder" :class="`mt-3 text-center text-sm ${applyThemeClasses('text.primary')}`">
          Most Active: <span class="font-semibold">{{ overallBreedingStats.mostActiveBreeder.name }}</span> ({{ overallBreedingStats.mostActiveBreeder.count }} breedings)
        </div>
      </div>
      
      <!-- Player Breeding Income -->
      <div v-if="unreadBreedingIncome.length > 0" :class="`mb-6 p-4 rounded-lg border ${applyThemeClasses('layout.secondaryBackground')} ${applyThemeClasses('layout.border')}`">
        <div class="flex items-center justify-between mb-3">
          <h3 :class="`text-lg font-semibold ${applyThemeClasses('text.primary')}`">
            💰 Your Breeding Income ({{ unreadBreedingIncome.length }})
          </h3>
          <button 
            @click="markAllBreedingIncomeAsRead"
            :class="`text-sm px-3 py-1 rounded ${applyThemeClasses('button.secondary')}`"
          >
            Mark all read
          </button>
        </div>
        
        <div class="space-y-2 max-h-40 overflow-y-auto">
          <div 
            v-for="notification in unreadBreedingIncome.slice(0, 5)"
            :key="notification.id"
            :class="`p-3 rounded-lg ${applyThemeClasses('layout.card')} border ${applyThemeClasses('layout.border')}`"
          >
            <div class="flex justify-between items-start">
              <div>
                <p :class="`font-medium ${applyThemeClasses('text.primary')}`">{{ notification.chocoboName }}</p>
                <p :class="`text-sm ${applyThemeClasses('text.secondary')}`">
                  Earned {{ notification.amount.toLocaleString() }}G from breeding
                </p>
              </div>
              <div :class="`text-xs ${applyThemeClasses('text.secondary')}`">
                {{ formatNotificationDate(notification.day).date }}
                <span v-if="formatNotificationDate(notification.day).daysAgo > 0">
                  ({{ formatNotificationDate(notification.day).daysAgo }} days ago)
                </span>
              </div>
            </div>
          </div>
          
          <div v-if="unreadBreedingIncome.length > 5" :class="`text-sm text-center ${applyThemeClasses('text.secondary')}`">
            +{{ unreadBreedingIncome.length - 5 }} more notifications...
          </div>
        </div>
      </div>
      
      <div class="space-y-2">
        <div 
          v-for="(ranches, region) in ranchesByRegion" 
          :key="region"
          class="border rounded-lg overflow-hidden"
        >
          <!-- Region Header - Always Visible -->
          <button 
            @click="toggleRegion(region)"
            class="w-full p-4 flex items-center justify-between transition-colors"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between">
                <div class="w-full">
                  <h3 :class="applyThemeClasses('text.primary')">{{ getRegionName(region) }}</h3>
                  <p :class="applyThemeClasses('text.secondary')">{{ ranches.length }} registered ranches</p>
                </div>
              </div>
            </div>
            
            <!-- Accordion Arrow -->
            <svg 
              class="w-5 h-5 text-gray-500 transform transition-transform duration-200"
              :class="{ 'rotate-180': expandedRegions.includes(region) }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Expandable Content -->
          <div 
            v-show="expandedRegions.includes(region)"
            class="divide-y"
          >
            <!-- Ranch List -->
            <div 
              v-for="ranch in ranches" 
              :key="ranch.id"
              class="p-4"
            >
              <!-- Ranch Header -->
              <div class="flex justify-between items-start mb-4">
                <div>
                  <h4 :class="applyThemeClasses('text.primaryLow')">{{ ranch.ranchName }}</h4>
                  <p :class="applyThemeClasses('text.secondaryBold')">Owner: {{ ranch.name }}</p>
                  <div class="flex space-x-2 mt-1">
                    <span :class="applyThemeClasses('text.secondary')">
                      Chocobos: {{ ranch.numChocobos }}
                    </span>
                    <span 
                      :class="[ranch.wealthStatus.color, 'px-2 py-0.5 text-xs rounded-full']"
                    >
                      {{ ranch.wealthStatus.label }}
                    </span>
                  </div>
                  <!-- Breeding Statistics -->
                  <div v-if="getOwnerBreedingStats(ranch).totalBreedings > 0" class="mt-2 text-xs space-y-1">
                    <div class="flex items-center space-x-3 text-gray-600">
                      <span class="flex items-center space-x-1">
                        🐣 <span>{{ getOwnerBreedingStats(ranch).totalBreedings }} bred</span>
                      </span>
                      <span v-if="getOwnerBreedingStats(ranch).activeBreedings > 0" class="flex items-center space-x-1">
                        ⏳ <span>{{ getOwnerBreedingStats(ranch).activeBreedings }} breeding</span>
                      </span>
                      <span v-if="getOwnerBreedingStats(ranch).studIncome > 0" class="flex items-center space-x-1">
                        💰 <span>{{ getOwnerBreedingStats(ranch).studIncome.toLocaleString() }}G stud fees</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Chocobo Grid -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  v-for="chocobo in ranch.chocobos"
                  :key="chocobo.id"
                  class="flex items-center gap-3 p-2 border rounded-lg"
                >
                  <div 
                    :class="[getChocoboColorClass(chocobo.color), 'w-10 h-10 rounded-lg shrink-0']"
                  ></div>
                  <div>
                    <div class="font-medium">
                      <span :class="applyThemeClasses('text.primaryLow')">{{ chocobo.name }}</span>
                    </div>
                    <div class="text-sm">
                      <span :class="applyThemeClasses('text.secondary')">Level {{ chocobo.level }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>